import {
  Button,
  CloseButton,
  Flex,
  Heading,
  Modal,
  SimpleSelect,
  Spinner,
  Text,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { CourseService } from "../../services/CourseService";
import { UserService } from "../../services/UserService";
import { FeatureContext, UserDetailContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { MULTI, Selector } from "../Selectors/Selector";

const SettingsModal = ({
  open,
  setOpen,
  userInfo,
  departments,
  setRows,
  getRole,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const features = useContext(FeatureContext);
  const showNotification = useNotification();
  const [selectedRole, setSelectedRole] = useState(userInfo.roleId);
  const [selectedDepartments, setSelectedDepartments] = useState(
    departments.filter((d) => userInfo.departments.includes(d.id))
  );
  const [userCourses, setUserCourses] = useState(null);
  const [initialCourses, setInitialCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lockedCourses, setLockedCourses] = useState([]);
  const [lockedDepts, setLockedDepts] = useState([]);
  const [pcUserDepts, setPcUserDepts] = useState([]);
  const [initialDepartments, setInitialDepartments] = useState(
    userInfo.roleId != "ac"
      ? departments.filter((d) => userInfo.departments.includes(d.id))
      : []
  );

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  useEffect(() => {
    setSelectedRole(userInfo.roleId);
    setSelectedDepartments(
      departments.filter((d) => userInfo.departments.includes(d.id))
    );
    setInitialDepartments(
      userInfo.roleId !== "ac"
        ? departments.filter((d) => userInfo.departments.includes(d.id))
        : []
    );

    // reset course-related state
    setUserCourses(null);
    setInitialCourses([]);
    setSelectedCourses([]);
  }, [userInfo]);

  useEffect(() => {
    if (finalizeFlag && open && selectedRole != "faculty" && !userCourses) {
      setLoadingCourses(true);
      const initialSelectedCourses = userInfo.permissions?.coordinatedCourses
        ? userInfo.permissions?.coordinatedCourses
        : [];
      CourseService.getContextForCourseCoordinatorAssignment(userInfo.canvasId)
        .then((courses) => {
          // returns all courses userInfo.canvasId teaches, where courses the requesting coordinator teaches are minimal
          const sharedCourses = [];
          const lockedCourses = [];
          if (courses && courses.length > 0) {
            courses = courses.filter(
              (course) => !!course.academic_subject?.title
            );
            for (const course of courses) {
              if (
                userDetails.isAC() ||
                (userDetails.isPC() &&
                  userDetails.ac_subjects?.includes(
                    course.academic_subject.title
                  ))
              ) {
                sharedCourses.push({
                  id: course.id,
                  label: course.code,
                  subject: course.academic_subject.title,
                });
              } else if (
                userDetails.isPC() &&
                userInfo.permissions?.coordinatedCourses?.includes(course.id)
              ) {
                lockedCourses.push({
                  id: course.id,
                  label: course.code,
                  subject: course.academic_subject.title,
                });
              }
            }
          }
          setUserCourses(sharedCourses);
          setLockedCourses(lockedCourses);
          setInitialCourses([
            ...sharedCourses.filter((course) =>
              initialSelectedCourses.includes(course.id)
            ),
            ...lockedCourses.filter((course) =>
              initialSelectedCourses.includes(course.id)
            ),
          ]);
          setSelectedCourses([
            ...sharedCourses.filter((course) =>
              initialSelectedCourses.includes(course.id)
            ),
            ...lockedCourses.filter((course) =>
              initialSelectedCourses.includes(course.id)
            ),
          ]);
        })
        .finally(() => setLoadingCourses(false));
    }
    if (userDetails.isPC()) {
      const cannotRemove = userInfo.departments
        .filter((id) => !userDetails.ac_subjects.includes(id))
        .map((id) => departments.find((d) => d.id === id));
      const canAlter = departments.filter((dept) =>
        userDetails.ac_subjects?.includes(dept.id)
      );
      setLockedDepts(cannotRemove);
      setPcUserDepts(canAlter);
    }
  }, [open, selectedRole, userInfo.canvasId, finalizeFlag]);

  async function handleSubmit() {
    try {
      setSubmitting(true);
      const selectedDepartmentIds = selectedDepartments.map((d) => d.id);
      const selectedCourseIds = selectedCourses.map((c) => c.id);
      await UserService.setAllUserProperties(
        userInfo.canvasId,
        selectedRole,
        selectedDepartmentIds.toString() !== userInfo.departments.toString() // conditional because don't store in neptune, and want to avoid unecessary data warehouse updates
          ? selectedDepartmentIds
          : null,
        { coordinatedCourses: selectedCourseIds }
      );
      setInitialCourses(selectedCourses);
      setInitialDepartments(
        userInfo.roleId != "ac"
          ? departments.filter((d) => userInfo.departments.includes(d.id))
          : []
      );
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.canvasId === userInfo.canvasId
            ? {
                ...row,
                roleId: selectedRole,
                role: getRole(selectedRole).toString(),
                departments: selectedDepartmentIds,
                permissions: {
                  ...row.permissions,
                  coordinatedCourses: selectedCourseIds,
                },
              }
            : row
        )
      );
      setOpen(false);
      showNotification(t("notifications.user.settingsUpdateSuccess"));
    } catch {
      showNotification(t("notifications.user.settingsUpdateError"), ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  function handleRoleSelect(value) {
    const currentRole = selectedRole; // role of the user being edited

    if (userDetails.isPC() && !canPCReassignRole(currentRole, value)) {
      showNotification(
        "PCs can only make changes to users with Course Coordinator or Faculty roles.",
        ERROR
      );
    } else {
      setSelectedRole(value);
      if (value === "ac") {
        setSelectedDepartments([]);
      }
      if (value == "faculty") {
        setSelectedCourses([]);
      }
    }
  }

  function canPCReassignRole(currentRole, newRole) {
    // PC can only reassign users with these current roles:
    const allowedCurrentRoles = [null, "", "cc", "faculty"];

    // PC can only assign to these roles:
    const allowedTargetRoles = ["cc", "faculty"];

    const canModifyUser = allowedCurrentRoles.includes(currentRole);
    const canAssignTargetRole = allowedTargetRoles.includes(newRole);

    return canModifyUser && canAssignTargetRole;
  }

  function handleDepartmentSelect(selectedDeptIds) {
    if (userDetails.isPC()) {
      const removedDept = initialDepartments.filter(
        (dept) => !selectedDeptIds.includes(dept.id)
      );
      if (
        removedDept.length > 0 &&
        !userDetails.ac_subjects.includes(removedDept[0].id)
      ) {
        showNotification(
          "PC User cannot remove this subject association.",
          ERROR
        );
      }
    }
    const selectedDepts = departments.filter((d) =>
      selectedDeptIds.includes(d.id)
    );
    const union = [...new Set([...lockedDepts, ...selectedDepts])];
    setSelectedDepartments(union);

    setSelectedCourses((initialCourses) =>
      initialCourses.filter((course) =>
        union.some((dept) => dept.id === course.subject)
      )
    );
  }

  function handleCourseSelect(selectedCourseIds) {
    if (userDetails.isPC()) {
      const removedCourse = initialCourses.filter(
        (course) => !selectedCourseIds.includes(course.id)
      );
      if (removedCourse && lockedCourses?.includes(removedCourse[0])) {
        showNotification(
          "PC User cannot remove this course association.",
          ERROR
        );
      }
    }
    setSelectedCourses(
      userCourses
        .filter((course) => selectedCourseIds.includes(course.id))
        .concat(lockedCourses)
    );
  }

  return (
    <Modal
      open={open}
      size="medium"
      label={t("user.manageUserSettings", { name: userInfo.fullName })}
      onDismiss={() => setOpen(false)}
      onExited={() => {
        setSelectedRole(userInfo.roleId);
        setSelectedDepartments(
          departments.filter((d) => userInfo.departments.includes(d.id))
        );
        setSelectedCourses(initialCourses);
      }}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading level="h2" as="h1">
              {t("user.manageUserSettings", { name: userInfo.fullName })}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              offset="small"
              onClick={() => setOpen(false)}
              screenReaderLabel={t("global.close")}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" gap="medium">
          <Flex.Item size="100%">
            <SimpleSelect
              renderLabel={t("user.role")}
              id={"role-selector" + userInfo.canvasId}
              value={selectedRole}
              onChange={(_e, { value }) => handleRoleSelect(value)}
            >
              <SimpleSelect.Option id={"ac"} value={"ac"}>
                {t("user.assessmentCoordinator")}
              </SimpleSelect.Option>
              {finalizeFlag && (
                <SimpleSelect.Option id={"cc"} value={"cc"}>
                  {t("user.courseCoordinator")}
                </SimpleSelect.Option>
              )}
              <SimpleSelect.Option id={"faculty"} value={"faculty"}>
                {t("user.faculty")}
              </SimpleSelect.Option>
              <SimpleSelect.Option id={"pc"} value={"pc"}>
                {t("user.programCoordinator")}
              </SimpleSelect.Option>
            </SimpleSelect>
          </Flex.Item>
          {(userDetails.isAC() || userDetails.isPC()) &&
            selectedRole != "ac" && (
              <Flex.Item size="100%">
                <Selector
                  type={MULTI}
                  id={"department-selector" + userInfo.canvasId}
                  label={t("subject.selector")}
                  options={
                    userDetails.isAC()
                      ? departments
                      : userInfo.departments
                        ? [...new Set([...pcUserDepts, ...lockedDepts])]
                        : lockedDepts
                  }
                  selectHandler={handleDepartmentSelect}
                  selectedOptions={
                    selectedRole != "ac"
                      ? selectedDepartments?.map((d) => d.id) || []
                      : []
                  }
                  interaction={
                    (userDetails.isAC() || userDetails.isPC()) &&
                    selectedRole != "ac"
                  }
                />
              </Flex.Item>
            )}
          {finalizeFlag && (
            <Flex.Item size="100%">
              <Selector
                type={MULTI}
                id={"course-selector" + userInfo.canvasId}
                label={
                  <Text>
                    {t("user.coordinatedCourses")}{" "}
                    {loadingCourses && (
                      <Spinner
                        size="x-small"
                        renderTitle={t("global.pleaseWait")}
                      />
                    )}
                  </Text>
                }
                options={
                  selectedRole !== "ac" && selectedRole !== "faculty"
                    ? userCourses?.filter((c) =>
                        selectedDepartments.some((d) => d.id === c.subject)
                      ).length > 0
                      ? Array.from(
                          new Set([
                            ...userCourses.filter(
                              (c) =>
                                selectedDepartments.some(
                                  (d) => d.id === c.subject
                                ),
                              ...lockedCourses
                            ),
                          ])
                        )
                      : lockedCourses
                    : selectedRole == "ac"
                      ? userCourses || []
                      : []
                }
                selectHandler={handleCourseSelect}
                selectedOptions={selectedCourses?.map((c) => c.id) || []}
                interaction={!loadingCourses && selectedRole !== "faculty"}
              />
            </Flex.Item>
          )}
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setOpen(false)} margin="0 x-small 0 0">
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          type="button"
          onClick={handleSubmit}
          interaction={submitting ? "disabled" : "enabled"}
        >
          {submitting ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SettingsModal;
